import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// A new page starts at the top; back and forward (and a reload) bring the page back to where it was
// left, e.g. the program after a speaker's profile. The browser's own scroll restoration is off, as it would put back an old position on a page
// that is still loading: here the position waits until the page has grown tall enough (the event page
// loads its program after itself), for at most RESTORE_MS, and gives up as soon as the visitor scrolls.
if (typeof window !== 'undefined' && window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const RESTORE_MS = 5000;
const STORE = 'heartport.scroll';

const reducedMotion = () => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Where each history entry (location.key) was left, also across a reload (sessionStorage).
const positions = (() => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORE)) || {};
  } catch (e) {
    return {};
  }
})();
const keep = () => {
  try {
    window.sessionStorage.setItem(STORE, JSON.stringify(positions));
  } catch (e) { /* private mode: only for this page's life */ }
};

// A jump before the new page is painted, not a smooth scroll after it: the new page first shows its
// short loading state, and a smooth scroll still running when its content comes in stops part way,
// often at the bottom of the page. What softens the change instead is the new page fading in (the
// site's <main>, App's ContentWrapper), not on the first load and not under prefers-reduced-motion.
function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  // The path of the page shown before; it starts as the first page's own, so that one does not fade.
  // Compared rather than a "first time" flag, which React's StrictMode (a second run of each effect on
  // mount) would clear before the second run.
  const previous = useRef(pathname);
  // The fade still running when the next page comes (quick clicks): ended before the new one starts.
  const fade = useRef(null);
  // The history entry being shown, whose position the scroll listener records.
  const current = useRef(key);
  const restoring = useRef(false);
  const first = useRef(true);

  // The position is noted on every scroll, and again when a link is clicked or the back button pressed
  // (the old page still shown then): a tab in the background gets no scroll events.
  useEffect(() => {
    let timer = null;
    const record = () => {
      if (restoring.current) return;
      positions[current.current] = window.scrollY;
      if (timer == null) timer = setTimeout(() => { timer = null; keep(); }, 200);
    };
    window.addEventListener('scroll', record, { passive: true });
    document.addEventListener('click', record, true);
    window.addEventListener('popstate', record, true);
    return () => {
      window.removeEventListener('scroll', record);
      document.removeEventListener('click', record, true);
      window.removeEventListener('popstate', record, true);
      clearTimeout(timer);
      keep();
    };
  }, []);

  useLayoutEffect(() => {
    current.current = key;
    const saved = navigationType === 'POP' ? positions[key] : undefined;
    const pathChanged = previous.current !== pathname;
    // A new page, the first one or one to put back starts at the top; a change of the hash alone
    // (an anchor on the same page) is left to the page.
    if (pathChanged || first.current || saved > 0) window.scrollTo(0, 0);
    first.current = false;

    let timer = null;
    let stop = () => {};
    if (saved > 0) {
      // Put the position back once the page is tall enough, until the visitor takes over.
      restoring.current = true;
      const started = Date.now();
      const end = () => {
        restoring.current = false;
        clearInterval(timer);
        ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((t) => window.removeEventListener(t, end));
      };
      const attempt = () => {
        const room = document.documentElement.scrollHeight - window.innerHeight;
        if (room >= saved) {
          window.scrollTo(0, saved);
          end();
        } else if (Date.now() - started > RESTORE_MS) {
          window.scrollTo(0, Math.max(room, 0));
          end();
        }
      };
      ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((t) => window.addEventListener(t, end, { passive: true }));
      timer = setInterval(attempt, 100);
      attempt();
      stop = end;
    }

    if (pathChanged) {
      previous.current = pathname;
      const main = document.querySelector('main');
      if (main && typeof main.animate === 'function' && !reducedMotion()) {
        if (fade.current) fade.current.cancel();
        fade.current = main.animate(
          [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
          { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
        );
      }
    }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per history entry
  }, [key]);

  return null;
}

export default ScrollToTop;
